<script setup>
    // Vue
    import { defineExpose } from 'vue';
    import { ref, defineEmits, inject } from 'vue';

    // Component
    import PasswordInput from '@/components/PasswordInputComponent.vue';
    import Tooltip from '@/components/TooltipComponent.vue';

    // Store
    import { useUserLoginPasswordUpdateStore } from '@/stores/modal/userLoginPasswordUpdateStore';
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useLoginStore } from '@/stores/login/loginStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Utils
    import Utils from '@/commonProject/helpers/utils.js';

    // Else
    import i18n from '@/common/i18n';
    
    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // ----------------------------
    // パスワード変更
    // ----------------------------
    const userLoginPasswordStore = useUserLoginPasswordUpdateStore();
    const commonStore = useCommonStore();
    const loginStore = useLoginStore();
    const passwordForm = ref({ password: '', newPassword: '', newPasswordConfirm: '' });

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    /**
     * 親画面からセッション詳細モーダルを開くイベントが実行された場合の処理。
     * @param {String} API_URL APIをコールするためのURL座標。今は使っていない。
     * @returns なし
     */
     async function openModal(/*API_URL*/) {
        // 本モーダルを表示した際に、パスワード入力欄HTML要素の初期化処理を実行する。
        passwordInputComponent1.value.resetInput();
        passwordInputComponent2.value.resetInput();
        passwordInputComponent3.value.resetInput();
    }
    /**
     * 入力情報リセット
     */
    function resetPasswordFrom() {
        passwordForm.value.password = '';
        passwordForm.value.newPassword = '';
        passwordForm.value.newPasswordConfirm = '';
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        // 現在のパスワード
        if (Utils.isNullEmpty(passwordForm.value.password)) {
            message += i18n.global.t('userLoginPasswordUpdate.message.message1') + '\n';
        } else {
            if (Utils.checkLengthOfPassword(passwordForm.value.password)) {
                message += i18n.global.t('userLoginPasswordUpdate.message.message2') + '\n';
            }
            if (!Utils.isMatchPatternAlphabetNumberSymbol(passwordForm.value.password)) {
                message += i18n.global.t('userLoginPasswordUpdate.message.message3') + '\n';
            }
        }
        // 新しいパスワード
        if (Utils.isNullEmpty(passwordForm.value.newPassword)) {
            message += i18n.global.t('userLoginPasswordUpdate.message.message4') + '\n';
        } else {
            if (Utils.checkLengthOfPassword(passwordForm.value.newPassword)) {
                message += i18n.global.t('userLoginPasswordUpdate.message.message5') + '\n';
            }
            if (!Utils.isMatchPatternAlphabetNumberSymbol(passwordForm.value.newPassword)) {
                message += i18n.global.t('userLoginPasswordUpdate.message.message6') + '\n';
            }
        }
        // 新しいパスワード確認
        if (Utils.isNullEmpty(passwordForm.value.newPasswordConfirm)) {
            message += i18n.global.t('userLoginPasswordUpdate.message.message7') + '\n';
        } else {
            if (Utils.checkLengthOfPassword(passwordForm.value.newPasswordConfirm)) {
                message += i18n.global.t('userLoginPasswordUpdate.message.message8') + '\n';
            }
            if (!Utils.isMatchPatternAlphabetNumberSymbol(passwordForm.value.newPasswordConfirm)) {
                message += i18n.global.t('userLoginPasswordUpdate.message.message9') + '\n';
            }
        }
        // パスワードの一致
        if (!Utils.isStringMatch(passwordForm.value.newPassword, passwordForm.value.newPasswordConfirm)) {
            message += i18n.global.t('userLoginPasswordUpdate.message.message10') + '\n';
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    // ----------------------------
    // 親画面で実装必須関数
    // ----------------------------
    const emit = defineEmits(['userLoginPasswordUpdateModal:cancel', 'userLoginPasswordUpdateModal:update']);
    function cancel() {
        resetPasswordFrom();
        // 親画面のモーダル非表示処理を呼び出す
        emit('userLoginPasswordUpdateModal:cancel');
    }
    async function update() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        const response = await userLoginPasswordStore.updateNewCognitoPasswordAfterLoginAction(passwordForm.value.password, passwordForm.value.newPassword);
        switch (response.status) {
            case 'change-password-success':
                {
                    // ユーザーパスワード更新完了APIをコールする。クエリパラメーターの条件は設定しない。
                    const response1 = await loginStore.postUserLoginPasswordUpdateedDateUpdateAction(commonStore.API_URL, null);

                    if (response1.success) {
                        resetPasswordFrom();
                        // 親画面の更新後処理を呼び出す
                        emit('userLoginPasswordUpdateModal:update');
                    } else {
                        messageDialog('error', response1.errorMessage);
                        if (response1.redirect) {
                            location.href = response1.redirectUrl;
                        }
                        return;
                    }
                }

                break;
            case 'change-password-failed':
                // 通常のパスワードエラー。
                messageDialog('error', i18n.global.t('common.message.cognitoError'));
                break;
            case 'no-password-reuse':
                // パスワードの使いまわしエラー。
                messageDialog('error', i18n.global.t('common.message.noPasswordReuse'));
                break;
            case 'NO SESSION':
                messageDialog('error', i18n.global.t('common.message.errorSession'));
                break;
            default:
                // 何もしない。
                break;
        }
    }

    defineExpose({
        openModal,
    });

    // パスワードHTML要素コンポーネントを宣言する。
    // 複数存在する場合は、ユニークなコンポーネント名を設定する。
    const passwordInputComponent1 = ref(null);
    const passwordInputComponent2 = ref(null);
    const passwordInputComponent3 = ref(null);
</script>

<template>
    <article id="modalChangePassword" class="modal medium" :style="{ display: 'block' }">
        <h3 class="textCenter">{{ $t('userLoginPasswordUpdate.parts.modalTitle1') }}</h3>
        <section class="modalContents" style="height: calc(100% - 145px)">
            <section class="form dtWidth180" style="height: auto">
                <!-- 現在のパスワード -->
                <dl class="noBorder">
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('userLoginPasswordUpdate.parts.dl1') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <PasswordInput ref="passwordInputComponent1" v-model:input="passwordForm.password" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 新しいパスワード -->
                <dl class="noBorder">
                    <dt>
                        <ul class="formLabel">
                            <li style="align-items: center; display: flex">
                                <label>{{ $t('userLoginPasswordUpdate.parts.dl2') }}</label>
                                <Tooltip :divLeft="43" :divWidth="250">{{ $t('userLoginPasswordUpdate.parts.blowoutInformation1') }}</Tooltip>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <PasswordInput ref="passwordInputComponent2" v-model:input="passwordForm.newPassword" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <!-- 新しいパスワード(確認) -->
                <dl>
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('userLoginPasswordUpdate.parts.dl3') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <PasswordInput ref="passwordInputComponent3" v-model:input="passwordForm.newPasswordConfirm" />
                            </li>
                        </ul>
                    </dd>
                </dl>
            </section>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <button type="button" class="btnNormal closeModal" @click="cancel">{{ $t('common.parts.cancel') }}</button>
                </li>
                <li>
                    <button type="button" class="btnPrimary closeModal" @click="update" :disabled="disabledStore.isDisabled">{{ $t('common.parts.entry') }}</button>
                </li>
            </ul>
        </section>
    </article>
</template>

<style>
    .passwordBlock .btnPassword {
        right: 8px;
    }
</style>
